import { Select, SelectOption, spacingHelper, useRecentSearches } from '@property.com.au/ui-kit';
import { push } from '@rea-argonaut/link';
import { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { DynamicContentsItem } from '../../app-kit/components/DynamicContent/types';
import { createSearchUrl } from '../../helpers/createSearchUrl';

import { getBudgetFiltersFromUrl } from '../../helpers/queryStringHelpers/getBudgetFiltersFromUrl';
import { MODAL_MAX_WIDTH, MODAL_PADDING, SearchModal } from '../SearchModal/SearchModal';
import { SelectWithFixedPositionOptions } from '../SelectWithFixedPositionOptions/SelectWithFixedPositionOptions';
import { getSelectOptionByString } from '../../helpers/getSelectOptionByString';
import { SearchPageFormLabel } from '../SearchpageFormLabel/SearchPageFormLabel';

interface BudgetModalContentProps {
  minPriceOptions: SelectOption[];
  maxPriceOptions: SelectOption[];
  minPrice: SelectOption | null;
  setMinPrice: React.Dispatch<React.SetStateAction<SelectOption | null>>;
  maxPrice: SelectOption | null;
  setMaxPrice: React.Dispatch<React.SetStateAction<SelectOption | null>>;
}

const Form = styled.div`
  display: flex;
  gap: ${spacingHelper('extraSmall')};

  > div {
    width: 50%;
  }
`;

export const BudgetModalContent: FunctionComponent<BudgetModalContentProps> = ({
  minPriceOptions,
  maxPriceOptions,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,
}) => {
  const FIELD_SIZE_VARIANT = 'medium';
  const HALF_GAP = `0.25rem`;
  const SELECT_WIDTH = `calc(${MODAL_MAX_WIDTH} / 2 - ${MODAL_PADDING} / 2 - ${HALF_GAP})`;
  const SELECT_MAX_WIDTH = `calc(50vw - ${MODAL_PADDING} / 2 - ${HALF_GAP})`;

  return (
    <Form>
      <SelectWithFixedPositionOptions width={SELECT_WIDTH} maxWidth={SELECT_MAX_WIDTH}>
        <Select
          options={minPriceOptions}
          id="SearchFilterMinPrices"
          placeholder=""
          onSelectedOptionChange={(selection?: SelectOption | null) => {
            if (selection) {
              setMinPrice(selection);
            }
          }}
          selectedOption={minPrice}
          label={<SearchPageFormLabel>Price min</SearchPageFormLabel>}
          sizeVariant={FIELD_SIZE_VARIANT}
        />
      </SelectWithFixedPositionOptions>
      <SelectWithFixedPositionOptions width={SELECT_WIDTH} maxWidth={SELECT_MAX_WIDTH}>
        <Select
          options={maxPriceOptions}
          placeholder=""
          id="SearchFilterMaxPrices"
          onSelectedOptionChange={(selection?: SelectOption | null) => {
            if (selection) {
              setMaxPrice(selection);
            }
          }}
          selectedOption={maxPrice}
          label={<SearchPageFormLabel>Price max</SearchPageFormLabel>}
          sizeVariant={FIELD_SIZE_VARIANT}
        />
      </SelectWithFixedPositionOptions>
    </Form>
  );
};

interface BudgetModalProps {
  onClose: () => void;
  searchQuery: string;
  saveRecentLocationSearch: ReturnType<typeof useRecentSearches>['saveRecentLocationSearch'];
  title: string;
  subtitle: string;
  footerContent: DynamicContentsItem[];
  minPriceOptions: SelectOption[];
  maxPriceOptions: SelectOption[];
}

export const BudgetModal: FunctionComponent<BudgetModalProps> = ({
  onClose,
  searchQuery,
  saveRecentLocationSearch,
  minPriceOptions,
  maxPriceOptions,
  ...rest
}) => {
  const { minPrice: minPriceFromUrl, maxPrice: maxPriceFromUrl } =
    getBudgetFiltersFromUrl(searchQuery);

  const [minPrice, setMinPrice] = useState<SelectOption | null>(
    getSelectOptionByString({
      value: minPriceFromUrl,
      options: minPriceOptions,
    }),
  );
  const [maxPrice, setMaxPrice] = useState<SelectOption | null>(
    getSelectOptionByString({
      value: maxPriceFromUrl,
      options: maxPriceOptions,
    }),
  );

  const onConfirm = () => {
    createSearchUrl({
      currentUrl: searchQuery,
      updates: {
        pageNumber: '',
        minPrice: minPrice?.value,
        maxPrice: maxPrice?.value,
      },
      historyPushFn: push,
      saveRecentLocationSearch,
    });
    onClose();
  };

  const minPriceNum = +(minPrice?.value ?? 0);
  const maxPriceNum = +(maxPrice?.value ?? 0);

  const disableConfirm = maxPriceNum > 0 && minPriceNum > maxPriceNum;
  return (
    <SearchModal
      opened
      onCancel={onClose}
      onConfirm={onConfirm}
      disableConfirm={disableConfirm}
      {...rest}
      showDialogBodyOverlay={false}
    >
      <BudgetModalContent
        minPriceOptions={minPriceOptions}
        maxPriceOptions={maxPriceOptions}
        minPrice={minPrice}
        setMinPrice={setMinPrice}
        maxPrice={maxPrice}
        setMaxPrice={setMaxPrice}
      />
    </SearchModal>
  );
};
